import React from 'react'
import CustomerTable from '../components/CustomerTable'
import CustomerCreateModal from '../components/CustomerCreateModal'
import CustomerEditModal from '../components/CustomerEditModal'
import { useCustomers } from '../hooks/useCustomers'
import type { Customer } from '../types'
import CustomerStatsCards from '../components/CustomerStatsCards'
import { useToast } from '@/shared/ui/Toast'
import { AlertModal } from '@/shared/ui/AlertModal'
import { BulkSelectBar } from '@/shared/ui/BulkSelectBar'
import { deleteCustomersBulk, syncAccountLatestSubscription } from '../services/customerService'
import { listPlans } from '@/modules/plans/services/planService'
import CustomerPasswordModal from '../components/CustomerPasswordModal'

export default function CustomersPage() {
  const { add: toast } = useToast()
  const { items, total, page, setPage, pageSize, search, setSearch, status, setStatus, planId, setPlanId, error, load, inactivateCustomer, activateCustomer, deleteCustomer, stats } = useCustomers()
  const [planOptions, setPlanOptions] = React.useState<Array<{ id: string; name: string }>>([])

  const [createOpen, setCreateOpen] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Customer | null>(null)
  const [pwdOpen, setPwdOpen] = React.useState(false)
  const [pwdTarget, setPwdTarget] = React.useState<Customer | null>(null)

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  React.useEffect(() => {
    // Limpa seleção ao trocar de página ou atualizar lista
    setSelectedIds(new Set())
  }, [page, items.map(i=>i.id).join(',')])

  React.useEffect(() => {
    ;(async () => {
      try {
        const { items } = await listPlans({ page: 1, pageSize: 200 })
        const active = items.filter(p => p.active)
        setPlanOptions(active.map(p => ({ id: p.id, name: p.name })))
      } catch {}
    })()
  }, [])

  const allOnPageSelected = items.length > 0 && items.every(i => selectedIds.has(i.id))
  function onToggleSelect(id: string, checked: boolean) {
    setSelectedIds(prev => { const n = new Set(prev); if (checked) n.add(id); else n.delete(id); return n })
  }
  function onToggleSelectPage(checked: boolean) {
    setSelectedIds(prev => {
      const n = new Set(prev)
      if (checked) items.forEach(i => n.add(i.id))
      else items.forEach(i => n.delete(i.id))
      return n
    })
  }

  async function handleToggleActive(c: Customer) {
    try {
      if (c.status === 'ativo') await inactivateCustomer(c.id)
      else await activateCustomer(c.id)
      await load()
    } catch (e: any) {
      toast({ title: 'Falha ao atualizar status', description: e?.message || 'Tente novamente', variant: 'error', soft: true })
    }
  }

  async function handleDelete(c: Customer) {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return
    try { await deleteCustomer(c.id); await load() }
    catch (e: any) { toast({ title: 'Falha ao excluir', description: e?.message || 'Tente novamente', variant: 'error', soft: true }) }
  }

  async function handleSync(c: Customer) {
    try { await syncAccountLatestSubscription(c.id); toast({ title: 'Sincronização concluída', variant: 'success', soft: true }); await load() }
    catch (e: any) { toast({ title: 'Falha ao sincronizar', description: e?.message || 'Tente novamente', variant: 'error', soft: true }) }
  }

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Clientes</h1>
        <p className="text-sm text-foreground/70">Administração de clientes (somente master).</p>
      </div>

      <CustomerStatsCards stats={stats} className="mb-6" />

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
      )}

      <BulkSelectBar
        selectedCount={selectedIds.size}
        totalCount={items.length}
        onSelectPage={() => setSelectedIds(new Set(items.map(i => i.id)))}
        onClear={() => setSelectedIds(new Set())}
        onDelete={() => setConfirmOpen(true)}
        className="mb-4"
      />

      <CustomerTable
        items={items}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={(v)=> { setStatus(v); setPage(1); load() }}
        planId={planId}
        onPlanChange={(v)=> { setPlanId(v); setPage(1); load() }}
        plans={planOptions}
        onCreate={() => setCreateOpen(true)}
        onEdit={(c) => { setEditing(c); setEditOpen(true) }}
        onChangePassword={(c) => { setPwdTarget(c); setPwdOpen(true) }}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        canBulkSelect={true}
        selectedIds={selectedIds}
        allOnPageSelected={allOnPageSelected}
        onToggleSelect={onToggleSelect}
        onToggleSelectPage={onToggleSelectPage}
        onSync={handleSync}
      />

      <CustomerCreateModal open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      <CustomerEditModal open={editOpen} onOpenChange={setEditOpen} customer={editing} onSaved={load} />
      <CustomerPasswordModal open={pwdOpen} onOpenChange={setPwdOpen} customer={pwdTarget} onSaved={() => { toast({ title: 'Senha atualizada', variant: 'success', soft: true }); setPwdTarget(null) }} />

      <AlertModal
        open={confirmOpen}
        onClose={() => !deleting && setConfirmOpen(false)}
        title="Excluir clientes selecionados?"
        description={
          <div className="space-y-2">
            <p>Você está prestes a excluir <strong>{selectedIds.size}</strong> cliente(s).</p>
            <ul className="list-disc pl-5 text-sm text-foreground/80">
              <li>Remove contas, assinaturas e vínculos (account_users).</li>
              <li>Remove perfis e o usuário do Supabase Auth.</li>
              <li>Ação irreversível. Assinaturas ativas serão finalizadas imediatamente.</li>
            </ul>
          </div>
        }
        variant="warning"
        confirmText={deleting ? 'Excluindo…' : 'Confirmar exclusão'}
        onConfirm={async () => {
          if (deleting) return
          setDeleting(true)
          try {
            await deleteCustomersBulk(Array.from(selectedIds))
            setSelectedIds(new Set())
            await load()
            toast({ title: 'Exclusão concluída', variant: 'success', soft: true })
          } catch (e: any) {
            toast({ title: 'Falha ao excluir', description: e?.message || 'Tente novamente', variant: 'error', soft: true })
          } finally {
            setDeleting(false)
            setConfirmOpen(false)
          }
        }}
      />
    </div>
  )
}
﻿
